/* eslint-disable no-extra-boolean-cast */
import { useAppDispatch } from '@store/hooks';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import { Form } from 'react-bootstrap';
import useGetBanks from '@hooks/Lookups/use-get-Banks';
import useGetAccounts from '@hooks/Finance/ChartAccountsDetails/useGetAccounts';
import Select from 'react-select';
import { addApBankAccounts, getApBankAccountsList } from '@store/Finance/AP/ApBankAccounts/ApBankAccountsSlice';
import Loading from '@components/Shared/Loader/Loading';
import { AddBankAccountsSchema } from '../../../ValidationForm/validationSchema';
import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';

const AddBankAccounts = () => {
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();
  const { OptionsAccounts, loading, error } = useGetAccounts();

  const { Banks, loading: loading2, error: error2 } = useGetBanks();

  const { OperatingUnitOptions, loading: loading1, error: error1 } = useGetOperatingUnitWithPermission();

  const formik = useFormik({
    initialValues: {
      ntxBankId: '',
      name: '',
      name2: '',
      description: '',
      bankAccount: '',
      accountId: '',
      accountReconcileId: '',
      iban: '',
      organizationId: ''
    },
    validationSchema: AddBankAccountsSchema(t),
    onSubmit: values => {
      dispatch(
        addApBankAccounts({
          name: values.name,
          name2: values.name2,
          ntxBankId: values.ntxBankId,
          bankAccount: values.bankAccount,
          iban: values.iban,
          description: values.description,
          organizationId: values.organizationId,

          accountId: values.accountId,
          accountReconcileId: values.accountReconcileId
        })
      )
        .unwrap()
        .then((res: any) => {
          if (res?.succeeded === true) {
            CustomAlert({ action: 'Add' });
            document.getElementById('closeModalForm')?.click();
            dispatch(getApBankAccountsList());
            formik.resetForm();
          } else {
            formik.setSubmitting(false);
            CustomAlert({ action: 'Error', msg: res?.message });
          }
        })
        .catch((error: any) => {
          formik.setSubmitting(false);
          CustomAlert({ action: 'Error' });
        });
    }
  });

  const { values, errors, touched } = formik;

  return (
    <>
      <Loading loading={loading || loading2} error={error || error2}>
        <Form onSubmit={formik.handleSubmit}>
          <div className='row'>
            <div className='col-xl-6'>
              <div className='form-group mb-3'>
                <label className='form-label'>{t('LocalName')}</label>
                <Form.Control
                  className='form-control  '
                  type='text'
                  autoComplete='off'
                  name='name2'
                  onChange={formik.handleChange}
                  value={formik.values.name2}
                  isInvalid={!!(formik.touched.name2 && formik.errors.name2)}
                />
                <div className='text-danger small'>{touched.name2 && errors.name2}</div>
              </div>
            </div>
            <div className='col-xl-6'>
              <div className='form-group mb-3'>
                <label className='form-label'>{t('GlobalName')}</label>
                <Form.Control
                  className='form-control  '
                  type='text'
                  autoComplete='off'
                  name='name'
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  isInvalid={!!(formik.touched.name && formik.errors.name)}
                />
                <div className='text-danger small'>{touched.name && errors.name}</div>
              </div>
            </div>

            
            {/* ✅ Operating Unit */}
            <div className='col-xl-6'>
              <div className='form-group mb-3'>
                <label className='form-label'>{t('OperatingUnit') || 'Operating Unit'}</label>
                <Select
                  classNamePrefix='react-select'
                  className={`w-100 ${
                    !!(touched.organizationId && errors.organizationId) ? 'border border-danger' : ''
                  }`}
                  isLoading={loading1}
                  isSearchable
                  isClearable
                  name='organizationId'
                  options={OperatingUnitOptions}
                  placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                  onChange={(option: any) => {
                    formik.setFieldValue('organizationId', option ? option.value : null);
                  }}
                  value={OperatingUnitOptions?.find((f: any) => f.value === values.organizationId) ?? null}
                />
                <div className='text-danger small'>{touched.organizationId && (errors.organizationId as any)}</div>
              </div>
            </div>

            <div className='col-xl-6 '>
              <div className='form-group mb-3'>
                <label className='form-label'>{t('Bank')}</label>
                <Form.Select
                  className='form-select'
                  name='ntxBankId'
                  onChange={formik.handleChange}
                  value={formik.values.ntxBankId || ''}
                  isInvalid={!!(formik.touched.ntxBankId && formik.errors.ntxBankId)}
                >
                  <option value=''>{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option>
                  {Banks &&
                    Banks?.map((item: any, idx: any) => (
                      <option key={++idx} value={item?.id}>
                        {i18n.language === 'ar' ? item?.name2 : item?.name}
                      </option>
                    ))}
                </Form.Select>
                <div className='text-danger small'>{touched.ntxBankId && errors.ntxBankId}</div>
              </div>
            </div>
            <div className='col-xl-6'>
              <label className='form-label'>{t('NoBank')}</label>
              <div className='input-group'>
                <Form.Control
                  className='form-control'
                  type='text'
                  autoComplete='off'
                  name='bankAccount'
                  onChange={formik.handleChange}
                  value={formik.values.bankAccount}
                  isInvalid={!!(formik.touched.bankAccount && formik.errors.bankAccount)}
                />
              </div>
              <div className='text-danger small'>{touched.bankAccount && errors.bankAccount}</div>
            </div>

            <div className='col-xl-6'>
              <label className='form-label'>{t('Iban')}</label>
              <div className='input-group flex-nowrap'>
                <Form.Control
                  className='form-control'
                  type='text'
                  autoComplete='off'
                  name='iban'
                  onChange={formik.handleChange}
                  value={formik.values.iban}
                  isInvalid={!!(formik.touched.iban && formik.errors.iban)}
                />
                <span className='input-group-text'>SA</span>
              </div>
              <div className='text-danger small'>{touched.iban && errors.iban}</div>
            </div>

            <div className='col-xl-6 '>
              <div className='form-group mb-3'>
                <label className='form-label'>{t('Accounts')}</label>
                <Select
                  classNamePrefix='react-select'
                  className={`w-100 ${
                    formik.touched.accountId && formik.errors.accountId ? 'border border-danger' : ''
                  } `}
                  isLoading={loading}
                  isSearchable={true}
                  isClearable
                  name='accountId'
                  options={OptionsAccounts}
                  placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                  onChange={(option: any) => {
                    formik.setFieldValue('accountId', option === null ? null : option.value);
                  }}
                  value={OptionsAccounts?.find(f => f.value === formik.values.accountId)}
                />
                <div className='text-danger small'>{touched.accountId && errors.accountId}</div>
              </div>
            </div>

            <div className='col-xl-6 '>
              <div className='form-group mb-3'>
                <label className='form-label'>{t('حساب التسوية')}</label>
                <Select
                  classNamePrefix='react-select'
                  className={`w-100 ${
                    formik.touched.accountId && formik.errors.accountId ? 'border border-danger' : ''
                  } `}
                  isLoading={loading}
                  isSearchable={true}
                  isClearable
                  name='accountReconcileId'
                  options={OptionsAccounts}
                  placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                  onChange={(option: any) => {
                    formik.setFieldValue('accountReconcileId', option === null ? null : option.value);
                  }}
                  value={OptionsAccounts?.find(f => f.value === formik.values.accountReconcileId)}
                />
                <div className='text-danger small'>{touched.accountReconcileId && errors.accountReconcileId}</div>
              </div>
            </div>

            {/* <div className='col-xl-12'>
              <div className='form-group mb-3'>
                <label className='form-label'>{t('Note')}</label>
                <Form.Control
                  className='form-control  '
                  type='text'
                  autoComplete='off'
                  name='description'
                  onChange={formik.handleChange}
                  value={formik.values.description}
                  isInvalid={!!(formik.touched.description && formik.errors.description)}
                />
              </div>
            </div> */}
          </div>

          <div className='text-center mt-2'>
            <div className='d-flex justify-content-between gap-2'>
              <button
                disabled={formik.isSubmitting}
                type='submit'
                className='btn btn-lg btn-theme mb-1 w-100 rounded-0'
              >
                {formik.isSubmitting ? (
                  <div className='spinner-border spinner-border-sm me-2'></div>
                ) : (
                  <i className='fas fa-lg fa-fw me-2 fa-plus'></i>
                )}
                {t('Add')}
              </button>
              <button
                type='button'
                className='btn btn-lg  btn-default mb-1 w-100 rounded-0'
                data-bs-dismiss='modal'
                id='closeModalForm'
              >
                <i className='fa fa-power-off fa-fw me-1'></i> {t('Cancel')}
              </button>
            </div>
          </div>
        </Form>
      </Loading>
    </>
  );
};

export default AddBankAccounts;
